import { apiClient } from "@/services/api";
import type {
  CompleteMultipartUploadInput,
  MultipartUploadCompletion,
  MultipartUploadInitiation,
  UploadedPart,
} from "@/types";

const PART_SIZE_BYTES = 10 * 1024 * 1024; // 10MB parts

const MIN_UPLOAD_SPEED_LIMIT_KBPS = 10;

/**
 * Failure kinds for a single multipart part upload. The service layer is
 * i18n-free; callers translate these into localized messages.
 */
export type MultipartPartFailureKind = "presign" | "upload" | "etag";

export class MultipartUploadPartError extends Error {
  readonly kind: MultipartPartFailureKind;
  readonly partNumber: number;

  constructor(kind: MultipartPartFailureKind, partNumber: number) {
    super(`Multipart upload failed at part ${partNumber} (${kind})`);
    this.name = "MultipartUploadPartError";
    this.kind = kind;
    this.partNumber = partNumber;
  }
}

/**
 * Best-effort abort of a multipart upload; failures are logged but never
 * mask the upload error that triggered the abort.
 */
export async function abortUpload(
  uploadId: string,
  key: string,
): Promise<void> {
  try {
    await apiClient.abortMultipartUpload({ uploadId, key });
  } catch (abortError) {
    console.error("Failed to abort upload:", abortError);
  }
}

export function initiateUpload(input: {
  filename: string;
  contentType: string;
  fileSize: number;
  sessionId: string;
}): Promise<MultipartUploadInitiation> {
  return apiClient.initiateMultipartUpload(input);
}

export function completeUpload(
  input: CompleteMultipartUploadInput,
): Promise<MultipartUploadCompletion> {
  return apiClient.completeMultipartUpload(input);
}

/**
 * Returns the configured upload speed limit in bytes per second, or 0 when no
 * limit is set. The env var VITE_VIDEO_UPLOAD_SPEED_LIMIT is expressed in kbps
 * (kilobits per second), matching standard ISP bandwidth units.
 */
export function getUploadSpeedLimitBytesPerSec(): number {
  const raw = import.meta.env.VITE_VIDEO_UPLOAD_SPEED_LIMIT;
  if (!raw) return 0;
  const kbps = parseFloat(raw);
  if (!isFinite(kbps) || kbps <= 0) return 0;
  return (Math.max(kbps, MIN_UPLOAD_SPEED_LIMIT_KBPS) * 1000) / 8; // kbps → bytes per second
}

/**
 * Wraps a Blob in a ReadableStream that emits data at most
 * `speedLimitBytesPerSec` bytes per second. Each chunk is `chunkSize` bytes;
 * between chunks the function sleeps long enough to honour the rate limit.
 */
function createThrottledStream(
  blob: Blob,
  speedLimitBytesPerSec: number,
): ReadableStream<Uint8Array> {
  const chunkSize = 16 * 1024; // 16 KB – small enough for smooth throttling
  return new ReadableStream<Uint8Array>({
    async start(controller) {
      const buffer = await blob.arrayBuffer();
      const data = new Uint8Array(buffer);
      let offset = 0;

      while (offset < data.length) {
        const end = Math.min(offset + chunkSize, data.length);
        controller.enqueue(data.slice(offset, end));
        offset = end;

        // Sleep for the time it should take to transmit `chunkSize` bytes at
        // the configured rate.  The last chunk may be smaller, but sleeping the
        // full chunk duration is a conservative, simple approximation.
        const delayMs = (chunkSize / speedLimitBytesPerSec) * 1000;
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
      }

      controller.close();
    },
  });
}

export function getVideoDurationSeconds(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute("src");
      video.load();
    };

    const settle = (duration?: number) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(duration);
    };

    const timer = window.setTimeout(() => {
      settle(undefined);
    }, 5000);

    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const { duration } = video;
      settle(Number.isFinite(duration) && duration > 0 ? duration : undefined);
    };
    video.onerror = () => {
      settle(undefined);
    };
    video.src = objectUrl;
  });
}

export interface UploadPartsProgress {
  /** 0-100 percentage of the whole file uploaded so far. */
  progress: number;
  speedBytesPerSec: number;
  etaSeconds: number;
}

export interface UploadPartsOptions {
  uploadId: string;
  key: string;
  onProgress?: (progress: UploadPartsProgress) => void;
  signal?: AbortSignal;
}

export interface UploadPartsResult {
  parts: UploadedPart[];
}

/**
 * Uploads a file in parts via presigned URLs for direct S3 upload.
 *
 * For each part: request a presigned URL from the backend, PUT the part
 * directly to S3, and scrape the ETag from the response headers. A failed
 * presign or PUT aborts the whole multipart upload; a missing ETag does not
 * (matching the historical behavior).
 */
export async function uploadParts(
  file: File,
  options: UploadPartsOptions,
): Promise<UploadPartsResult> {
  const { uploadId, key, onProgress, signal } = options;

  const totalParts = Math.ceil(file.size / PART_SIZE_BYTES);
  const startTime = Date.now();
  let totalUploadedBytes = 0;
  const uploadedParts: UploadedPart[] = [];

  // Read the speed limit once up front instead of once per part.
  const speedLimitBytesPerSec = getUploadSpeedLimitBytesPerSec();

  for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
    if (signal?.aborted) {
      throw new DOMException("Upload aborted", "AbortError");
    }

    const start = (partNumber - 1) * PART_SIZE_BYTES;
    const end = Math.min(start + PART_SIZE_BYTES, file.size);
    const part = file.slice(start, end);

    // Get presigned URL for this part from the backend
    let presignedUrl: string;
    try {
      const presigned = await apiClient.getPresignedPartUrl({
        uploadId,
        key,
        partNumber,
      });
      presignedUrl = presigned.presignedUrl;
    } catch {
      // Abort the multipart upload on failure
      await abortUpload(uploadId, key);
      throw new MultipartUploadPartError("presign", partNumber);
    }

    // Upload part directly to S3 using the presigned URL. This request
    // bypasses the API client on purpose: it targets S3/MinIO with a
    // presigned URL that carries its own authorization.
    const partBody: Blob | ReadableStream<Uint8Array> =
      speedLimitBytesPerSec > 0
        ? createThrottledStream(part, speedLimitBytesPerSec)
        : part;
    // `duplex: "half"` is required by the Fetch spec when the request body
    // is a ReadableStream; the property is not yet in TypeScript's lib types
    // so we carry it in a plain options object.
    const partFetchOptions = {
      method: "PUT",
      body: partBody,
      headers: { "Content-Type": "application/octet-stream" },
      ...(signal ? { signal } : {}),
      ...(speedLimitBytesPerSec > 0 ? { duplex: "half" as const } : {}),
    };
    const partResponse = await fetch(presignedUrl, partFetchOptions);

    if (!partResponse.ok) {
      // Abort the multipart upload on failure
      await abortUpload(uploadId, key);
      throw new MultipartUploadPartError("upload", partNumber);
    }

    // Get ETag from S3 response headers (strip surrounding quotes if present)
    const rawEtag = partResponse.headers.get("ETag");
    if (!rawEtag) {
      throw new MultipartUploadPartError("etag", partNumber);
    }
    uploadedParts.push({
      partNumber,
      etag: rawEtag.replace(/"/g, ""),
    });

    // Report progress, speed and ETA for the whole file
    totalUploadedBytes += part.size;
    const progress = Math.round((totalUploadedBytes / file.size) * 100);
    const currentTime = Date.now();
    const elapsedTime = (currentTime - startTime) / 1000; // seconds
    const speed = totalUploadedBytes / elapsedTime; // bytes per second
    const remainingBytes = file.size - totalUploadedBytes;
    const eta = remainingBytes / speed; // seconds
    onProgress?.({ progress, speedBytesPerSec: speed, etaSeconds: eta });
  }

  return { parts: uploadedParts };
}
